import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import dateFormat from 'dateformat';
import { COMPLETE, SAVING } from './hooks/useJobActions';
import useJobForm from './hooks/useJobForm';
import Form from '../form/Form';
import JobPostingFields from './JobPostingFields';
import CloseJobFields from './admin/CloseJobFields';
import ApplyButton from './public/ApplyButton';
import Applicants from './admin/Applicants';
import { defaultValues } from './assets/schemas/add/addSchema';
import model from './assets/models/model';
import {
  FILLED_CLOSED_OPTION_CLOSED,
  IS_OPEN_TRUE,
} from './assets/constants/jobConstants';

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    margin: theme.spacing(2),
  },
  dialogTitle: {
    paddingBottom: 0,
  },
  dialogContent: {
    '&:first-child': {
      paddingTop: 0,
    },
    height: theme.typography.pxToRem(536),
  },
  dateSummary: {
    display: 'block',
    color: theme.palette.text.disabled,
    fontSize: theme.typography.body2.fontSize,
  },
  tabs: {
    marginBottom: theme.spacing(3),
  },
  closeButton: {
    float: 'right',
  },
}));

const JobDialog = ({
  onComplete,
  onClose,
  open,
  selectedJobData,
  disabled,
  selectedJobToClose,
  selectedJobToReopen,
  onApplicantDialogOpen,
}) => {
  const classes = useStyles();
  const [title, setTitle] = useState('');
  const [dateSummary, setDateSummary] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const { methods, actions } = useJobForm(model, defaultValues);
  const { save, update, status } = actions;
  const { reset } = methods;

  const {
    id,
    jobId,
    postingDate,
    lastUpdate,
    jobTitle,
    isOpen,
    closedFilled,
    applicants = [],
  } = selectedJobData;

  const handleTabChange = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const localOnClose = useCallback(() => {
    setDateSummary('');
    setTitle('');
    setCurrentTab(0);
    reset(defaultValues);
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (status === COMPLETE) {
      onComplete();
      localOnClose();
    }
  }, [localOnClose, onComplete, status]);

  const cancel = useCallback(() => {
    localOnClose();
  }, [localOnClose]);

  useEffect(() => {
    if (open) {
      if (jobId) {
        const postedDate = new Date(postingDate);
        const lastUpdated = new Date(lastUpdate);
        setTitle(`${jobTitle} (${jobId})`);
        setDateSummary(
          `Posted: ${dateFormat(
            postedDate,
            'mm-dd-yyyy'
          )} - Updated: ${dateFormat(lastUpdated, 'mm-dd-yyyy')}`
        );
        reset(selectedJobData);
      } else {
        setTitle(`Add Job Posting`);
      }
    }
  }, [open, selectedJobData, reset, jobId, jobTitle, postingDate, lastUpdate]);

  return (
    <Dialog open={open} onClose={localOnClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <b>{title}</b>
        {/* Closing a job can only be done for open jobs by admins */}
        {!disabled && isOpen === IS_OPEN_TRUE && (
          <Button
            className={classes.closeButton}
            onClick={() => {
              selectedJobToClose(selectedJobData);
              localOnClose();
            }}
            color="primary"
            variant="outlined"
          >
            Close
          </Button>
        )}
        {/* Reopening a job can only be done for closed jobs with a closed/filled value of closed by admins */}
        {!disabled &&
          isOpen !== IS_OPEN_TRUE &&
          closedFilled === FILLED_CLOSED_OPTION_CLOSED && (
            <Button
              className={classes.closeButton}
              onClick={() => {
                selectedJobToReopen(selectedJobData);
                localOnClose();
              }}
              color="primary"
              variant="outlined"
            >
              Reopen
            </Button>
          )}
        <span className={classes.dateSummary}>{dateSummary}</span>
      </DialogTitle>
      <Form methods={methods}>
        <DialogContent
          className={!disabled && jobId ? classes.dialogContent : ''}
        >
          {/* Tabs only show for admins viewing an existing job */}
          {!disabled && jobId ? (
            <>
              <Tabs
                className={classes.tabs}
                value={currentTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="JOB POSTING" />
                <Tab label="APPLICANTS" />
                {/* Closeout tab only shows for closed jobs */}
                {isOpen !== IS_OPEN_TRUE && <Tab label="CLOSEOUT" />}
              </Tabs>
              {currentTab === 0 ? (
                <JobPostingFields disabled={isOpen !== IS_OPEN_TRUE} />
              ) : null}
              {currentTab === 1 ? (
                <Applicants
                  applicants={applicants}
                  id={id}
                  lastUpdate={lastUpdate}
                  onApplicantDialogOpen={onApplicantDialogOpen}
                  disabled={isOpen !== IS_OPEN_TRUE}
                  onComplete={onComplete}
                />
              ) : null}
              {currentTab === 2 ? (
                <Grid container spacing={2}>
                  <CloseJobFields disabled />
                </Grid>
              ) : null}
            </>
          ) : null}
          {/* Add job post */}
          {!disabled && !jobId ? <JobPostingFields /> : null}
          {/* Public view */}
          {disabled ? <JobPostingFields disabled={disabled} /> : null}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={cancel}>Cancel</Button>
          {!disabled &&
            ((currentTab === 0 && isOpen === IS_OPEN_TRUE) || !jobId) && (
              <Button
                onClick={jobId ? update : save}
                color="primary"
                variant="contained"
                disabled={status === SAVING}
              >
                Save
              </Button>
            )}
          {disabled && <ApplyButton jobId={jobId} jobTitle={jobTitle} />}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

JobDialog.defaultProps = {
  selectedJobData: {},
  disabled: false,
  selectedJobToClose: () => {},
  selectedJobToReopen: () => {},
  onApplicantDialogOpen: () => {},
};

JobDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedJobData: PropTypes.object,
  disabled: PropTypes.bool,
  selectedJobToClose: PropTypes.func,
  selectedJobToReopen: PropTypes.func,
  onApplicantDialogOpen: PropTypes.func,
};

export default JobDialog;
